import { create } from 'zustand';

import { toConsentDateLabel } from '../lib/consent-date';
import { splitTimeSink } from '../lib/time-sink';

/**
 * 온보딩에서 받는 프로필. 전부 선택이라 빈 문자열이 정상값이다.
 * `members` · `consents` 가 붙기 전까지 화면이 기대하는 모양을 여기서 고정한다.
 */
export type MemberProfile = {
  /**
   * 구조화 닉네임의 **자유입력 부분**. `jobRoleTag` 와 합쳐서 `회계#민지` 로 보인다.
   * 표시할 때는 직접 읽지 말고 `memberNickname()` 을 쓴다 — 태그가 빠진 이름이 새어 나간다.
   */
  name: string;
  /** 직무 — 업종보다 자료 주제를 잘 예측한다 */
  jobRole: string;
  /** 닉네임 앞에 붙는 짧은 직무 태그. 「기타」면 본인이 직접 적은 값이 들어온다 */
  jobRoleTag: string;
  /** 지금 가장 반복적으로 시간을 뺏는 업무 하나. 소분방 배정·콘텐츠 추천의 근거값 */
  topTimeSink: string;
  /** `topTimeSink` 가 「기타」일 때의 짧은 서술 */
  topTimeSinkOther: string;
  /** 자동화 경험 수준 — 자료 난이도를 여기에 맞춘다 */
  automationLevel: string;
  /**
   * 온보딩을 끝냈는지. 값이 채워졌는지로 대신 판정하지 않는다 —
   * 세 답이 다 있어도 저장이 실패한 회원이 있을 수 있고, 그 사람은 다시 물어야 한다.
   */
  isOnboarded: boolean;
};

export type Member = { email: string } & MemberProfile;

/**
 * 로그인 상태를 만드는 데 필요한 것 전부.
 *
 * 서버 응답을 그대로 받지 않고 이만큼만 받는다 — `auth.refresh`·`auth.me` 는 회원 id 와
 * 권한까지 주지만 소셜 가입 완료 응답에는 권한이 없다. 넓게 받으면 세 창구 중 하나를
 * 받을 수 없게 되고, 화면이 쓰는 것은 어차피 이 둘뿐이다.
 */
export interface SignedInMember {
  email: string;
  name: string;
  /**
   * 서버가 함께 준 온보딩 값. **소셜 가입 완료 응답에는 없다** — 그 응답이 말하는 것은
   * "회원이 만들어졌다"까지이고, 방금 만든 회원은 어차피 온보딩을 지나지 않았다.
   * 없으면 미완료로 본다. 반대로 두면 그 사람만 온보딩 화면을 영영 못 본다.
   */
  onboarding?: OnboardingSnapshot;
  /**
   * 서버가 함께 준 광고 수신 동의. **소셜 가입 완료 응답에는 없다** — 온보딩과 같은 이유다.
   * 없으면 꺼진 것으로 본다. 반대로 두면 동의한 적 없는 사람에게 광고를 보내게 된다.
   */
  marketingEmail?: MarketingConsentSnapshot;
}

/** 서버가 돌려주는 수신 동의 (`auth.refresh` 등의 `member.marketingEmail`). */
export interface MarketingConsentSnapshot {
  granted: boolean;
  /** tRPC 를 지나면 ISO 문자열로 도착한다. */
  occurredAt: string | null;
}

/**
 * 서버가 돌려주는 온보딩 값 (`auth.refresh` 등의 `member.onboarding`).
 *
 * 화면의 `MemberProfile` 과 모양이 다르다 — 저장은 `topTimeTask` 한 칸이고 화면은
 * 보기·자유입력 두 칸이다. 그 사이를 `splitTimeSink` 가 잇는다.
 */
export interface OnboardingSnapshot {
  jobRole: string | null;
  jobRoleTag: string | null;
  topTimeTask: string | null;
  automationLevel: string | null;
  isCompleted: boolean;
}

type SessionState = {
  member: Member | null;
  /** `consents.MARKETING_EMAIL` 의 현재 상태 */
  marketingEmailOptIn: boolean;
  /** 마케팅 동의를 마지막으로 표시한 시각 (표기용 문자열) */
  marketingConsentAt: string | null;

  /** 서버가 준 회원으로 로그인 상태를 만든다. 화면이 지어낸 값은 받지 않는다. */
  signIn: (member: SignedInMember) => void;
  signOut: () => void;
  /**
   * 온보딩 저장 결과를 세션에 반영한다. `signIn` 과 마찬가지로 **서버가 돌려준 회원만**
   * 받는다 — 화면이 자기가 보낸 값을 되쓰면 서버가 다듬은 값과 어긋난 채로 남는다.
   *
   * `signIn` 을 다시 부르지 않는 이유: 저쪽은 "다른 사람이 들어왔다"는 뜻이라
   * 동의 표시를 비운다. 여기서는 같은 사람이 답만 채운 것이다.
   */
  applyOnboarding: (member: SignedInMember) => void;
  setMarketingOptIn: (next: boolean, at: string) => void;
};

export const EMPTY_PROFILE: MemberProfile = {
  name: '',
  jobRole: '',
  jobRoleTag: '',
  topTimeSink: '',
  topTimeSinkOther: '',
  automationLevel: '',
  isOnboarded: false,
};

/**
 * 서버가 준 회원 → 화면이 쓰는 프로필.
 *
 * 온보딩을 지나지 않았으면 `member.name` 은 가입 때 넣은 임시 이름(이메일 아이디)이다.
 * 그대로 둔다 — 마이페이지가 그 값을 대체 표시로 쓰고, 온보딩이 끝나면 덮인다.
 */
const toProfile = (member: SignedInMember): MemberProfile => {
  const onboarding = member.onboarding;

  if (onboarding === undefined) {
    return { ...EMPTY_PROFILE, name: member.name };
  }

  return {
    name: member.name,
    jobRole: onboarding.jobRole ?? '',
    jobRoleTag: onboarding.jobRoleTag ?? '',
    ...splitTimeSink(onboarding.topTimeTask),
    automationLevel: onboarding.automationLevel ?? '',
    isOnboarded: onboarding.isCompleted,
  };
};

/**
 * 로그아웃 상태의 수신 동의 표시. 누구의 것도 아니므로 꺼짐이다.
 *
 * 예전에는 로그인 때도 이 값으로 되돌렸다 — 서버가 동의를 돌려주지 않아서였고, 그래서
 * 화면에서 켜도 다시 로그인하면 꺼진 채로 돌아왔다. 지금은 서버가 함께 준다.
 */
const signedOutConsent = () => ({
  marketingEmailOptIn: false,
  marketingConsentAt: null as string | null,
});

/**
 * 서버가 준 동의 → 화면이 쓰는 표시 2개.
 *
 * 없으면 **꺼짐**이다. 소셜 가입 완료 응답에는 이 값이 없고, 없는데 켜진 것으로 보면
 * 동의한 적 없는 사람에게 광고를 보내게 된다.
 */
const toConsentDisplay = (marketingEmail: MarketingConsentSnapshot | undefined) =>
  marketingEmail === undefined
    ? signedOutConsent()
    : {
        marketingEmailOptIn: marketingEmail.granted,
        // 거부 상태면 보여줄 동의일이 없다.
        marketingConsentAt: marketingEmail.granted
          ? toConsentDateLabel(marketingEmail.occurredAt)
          : null,
      };

/**
 * 로그인 세션 — **서버 세션을 비추는 거울**이다.
 *
 * 값을 채우는 문은 `signIn` 하나뿐이고, 그 인자는 서버가 돌려준 회원만 받는다
 * (`auth.refresh`·`auth.me`). 화면이 이메일 문자열만으로 로그인 상태를 만들 수 있으면
 * 그건 인증이 아니라 표시일 뿐이고, 실제로 그렇게 쓰이고 있었다.
 *
 * **브라우저 저장소에 남기지 않는다.** 남기면 서버 세션이 끝난 뒤에도 화면만 로그인
 * 상태로 남아, 다음 요청이 401 로 튕길 때까지 사용자에게 거짓말을 한다.
 * 새로고침으로 비는 것이 정상이며, 그때는 앱 부팅이 `auth.refresh` 로 다시 채운다.
 *
 * 마케팅 동의 표시 두 값은 **서버가 로그인 응답에 함께 실어 준다.** 예전에는 서버에
 * 자리가 없어 로그인 때마다 비웠고, 그래서 화면에서 켜도 다시 로그인하면 꺼진 채로
 * 돌아왔다. 지금은 `signIn` 이 서버 값을 받아 채운다.
 * 로그아웃은 여전히 비운다 — 남기면 앞 사람의 흔적이 다음 사람 화면에 보인다.
 */
export const useSession = create<SessionState>()((set) => ({
  member: null,
  ...signedOutConsent(),

  signIn: (member) =>
    set({
      member: { ...toProfile(member), email: member.email },
      ...toConsentDisplay(member.marketingEmail),
    }),

  signOut: () => set({ member: null, ...signedOutConsent() }),

  // 항상 새 객체를 만든다 — 기존 상태를 변형하면 구독자가 갱신을 못 받는다
  applyOnboarding: (member) =>
    set((state) =>
      state.member ? { member: { ...toProfile(member), email: state.member.email } } : state,
    ),

  setMarketingOptIn: (next, at) =>
    set({ marketingEmailOptIn: next, marketingConsentAt: next ? at : null }),
}));
